import usePrevious from "@/lib/use-previous";
import { ImageChatContent } from "@/services/promptEditor.service";
import IconButton from "@components/library/IconButton";
import { LoadingIcon } from "@components/library/Icons";
import { ImageModal } from "@components/llama/ImageModal";
import { SelectInput, TextInput } from "@components/molecules/FormikInput";
import { SelectGroup, SelectItem } from "@components/ui/select";
import { PhotographIcon, XIcon } from "@heroicons/react/outline";
import { useEffect, useState } from "react";
import { useDebounce } from "use-debounce";
import { UploadImageDropzoneWrapper } from "./UploadImageDropzoneWrapper";
import { ToastVariant, hlToast } from "@components/library/Toast";

interface Props {
  content: ImageChatContent;
  onChange: (content: ImageChatContent) => void;
  onDelete?: () => void;
}

export const ChatImage = ({ content, onChange, onDelete }: Props) => {
  const [error, setError] = useState<boolean>(false);
  const previousUrl = usePrevious(content.image_url.url);

  // Reset error state when the url changes.
  useEffect(() => {
    if (previousUrl !== content.image_url.url) {
      setError(false);
    }
  }, [content.image_url.url, previousUrl]);

  const [displayedUrl] = useDebounce(content.image_url.url, 500); // The actual image we want to display. (Debounced to prevent making tons of requests as the user types.)

  const [imageModalOpen, setImageModalOpen] = useState(false);

  return (
    <UploadImageDropzoneWrapper
      onDrop={(dataUrls) => {
        if (dataUrls.length > 1) {
          // This should never happen as we aren't passing in `multiple` to the dropzone.
          // By not passing in `multiple`, the dropzone becomes uninteractive when the user is dragging multiple files.
          // TODO: Consider if we want to allow dropping multiple images.
          hlToast({
            title: "Only one image allowed",
            description: "Please drop only one image here. To add multiple images, drop them on the parent message.",
            variant: ToastVariant.Error,
          });
          return;
        } else if (dataUrls.length === 0) {
          // This happens for some reason
          // When the user drops multiple files on this non-multiple dropzone, or when the user drops non-accepted files (e.g. non-image files)
          return;
        }

        onChange({ ...content, image_url: { url: dataUrls[0] } });
      }}
    >
      <div className="flex items-center gap-16 rounded-ms p-16 shadow-card-sm">
        <div className="relative flex h-56 w-56 shrink-0 items-center justify-center overflow-hidden rounded-ms">
          {error && (
            // Absolute positioning just in case both image and this icon are rendered.
            <div className="absolute inset-0 flex items-center justify-center bg-background-base-3 opacity-80">
              <PhotographIcon className="h-24 w-24 text-gray-300" />
            </div>
          )}
          {displayedUrl !== content.image_url.url && (
            <div className="absolute inset-0 flex items-center justify-center bg-background-base-3 opacity-80">
              <LoadingIcon className="h-20 w-20 opacity-50" />
            </div>
          )}
          <img
            onError={() => setError(true)}
            src={displayedUrl}
            alt=""
            className="cursor-pointer rounded-ms object-contain"
            onClick={() => setImageModalOpen(true)}
          />
        </div>
        <div className="flex grow gap-8">
          <div className="grow">
            {/* TODO: Fix CSP */}
            <TextInput
              label="URL"
              name="image_url"
              value={
                // Truncate the URL if it's too long (as it lags)
                content.image_url.url.length > 10000
                  ? content.image_url.url.slice(0, 100) + "..."
                  : content.image_url.url
              }
              onChange={(e) => {
                const url = e.target.value;
                return onChange({ ...content, image_url: { url } });
              }}
              placeholder="Type image URL"
            />
          </div>
          <div className="w-96">
            <SelectInput
              name="detail"
              label="Detail"
              value={content.image_url.detail || "auto"}
              setValue={(value) => onChange({ ...content, image_url: { ...content.image_url, detail: value as any } })}
            >
              <SelectGroup>
                <SelectItem value="auto">Auto</SelectItem>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectGroup>
            </SelectInput>
          </div>
        </div>
        <IconButton Icon={XIcon} onClick={onDelete} />
        <ImageModal
          image={content.image_url.url}
          alt=""
          open={imageModalOpen}
          onClose={() => setImageModalOpen(false)}
        />
      </div>
    </UploadImageDropzoneWrapper>
  );
};
